import { useMemo, type CSSProperties, type ReactNode } from "react";
import { ArrowUpRight, Minus, Plus } from "lucide-react";

import {
  landingContentRewards,
  landingDaylight,
  landingFixated,
  landingFlowChart,
  landingFlowDollar,
  landingFlowFolders,
  landingFlowRoute,
  landingFlowShield,
  landingFlowTarget,
  landingFlowUsers,
  landingFlowVideo,
  landingHeroBackground,
  landingHeroCountries,
  landingHeroCreatorPlatforms,
  landingHeroDailyViews,
  landingInstagram,
  landingQcode,
  landingShorts,
  landingSnapchat,
  landingSuperclip,
  landingTiktok,
  landingVyro,
  landingX,
  logoVector,
  patterns,
} from "@/lib/assets";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import { siteConfig } from "@/lib/seo/metadata";
import { Logo } from "@/shared/design-system/Logo";
import { Accordion, type AccordionItemConfig } from "@/shared/ui/accordion/Accordion";
import { CtaLink } from "@/shared/ui/cta-link";
import { ExternalLink } from "@/shared/ui/external-link";
import { InternalLink } from "@/shared/ui/internal-link";

import { landingFaqItems } from "@/lib/seo/landingFaqContent";
import { Navigation } from "@/shared/layout/Navigation";

import { clippingSteps, serviceOptions } from "./desktopContent";
import { DemoRequestForm } from "./DemoRequestForm";
import { ProductFeatures } from "./ProductFeatures";
import { articleRegistry, BLOG_DESCRIPTION, BLOG_PATH } from "@/features/blog/articleRegistry";
import { ArticleGrid } from "@/features/blog/components/ArticleGrid";
import "./desktop-landing.css";

type DesktopLandingProps = {
  baseAnalyticsElementId: AnalyticsElementId;
};

const schedulingUrl = siteConfig.contact.calendly;
/** Takes the supplied artwork down to rail size as one set, so its proportions survive. */
const RAIL_LOGO_SCALE = 0.15;
/**
 * The client logos the live site shows.
 *
 * `artworkHeight` is the logo's height in the artwork the set was supplied at, and it is what sizes
 * these against each other: a two-line lockup stands taller than a single-line wordmark, and the
 * wordmarks sit level. Scaling the whole column by `RAIL_LOGO_SCALE` keeps those relative sizes,
 * where a shared height would flatten them — it shrinks a stacked lockup's text to half the size of
 * its neighbours'. `aspect` is the source file's own ink ratio, so nothing is distorted.
 *
 * `aspect` holds only while a source is trimmed to its artwork: transparent padding inside one
 * makes that logo draw small, since the rail sizes the image box rather than the ink.
 */
const railLogos = [
  { name: "Content Rewards", src: landingContentRewards, artworkHeight: 320, aspect: 896 / 289 },
  // 259, not the artwork's full 280: that includes a "Media" subscript this wordmark omits, and
  // 259 is the mark-plus-wordmark the source actually draws.
  { name: "Daylight", src: landingDaylight, artworkHeight: 259, aspect: 1366 / 308 },
  { name: "Fixated", src: landingFixated, artworkHeight: 184, aspect: 177.164 / 20.7075 },
  { name: "QCODE", src: landingQcode, artworkHeight: 196, aspect: 1220 / 229 },
  // Level with Fixated, as the artwork has them.
  { name: "SuperClip", src: landingSuperclip, artworkHeight: 184, aspect: 1460 / 184 },
];
/** Enough copies that the widest viewport stays covered at both ends of the scroll; see `dl-logo-scroll`. */
const railRepeats = [0, 1, 2, 3];

function LandingImage({
  src,
  alt,
  width,
  height,
  className,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
}) {
  return (
    <img src={src} alt={alt} width={width} height={height} className={className} loading="lazy" decoding="async" />
  );
}

function ScheduleLink({ analyticsElementId, children }: { analyticsElementId: AnalyticsElementId; children: string }) {
  return (
    <CtaLink
      analyticsElementId={analyticsElementId}
      href={schedulingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="dl-cta"
    >
      {children}
    </CtaLink>
  );
}

function DesktopHero({ baseAnalyticsElementId }: DesktopLandingProps) {
  const elementId = (suffix: string) => deriveAnalyticsElementId(baseAnalyticsElementId, suffix);

  return (
    <section className="dl-hero" aria-labelledby="desktop-hero-heading">
      <div className="dl-hero-canvas">
        <div className="dl-hero-background" style={{ backgroundImage: `url(${landingHeroBackground})` }} />
        <LandingImage src={patterns} alt="" width={1880} height={1880} className="dl-hero-pattern" />
        <Navigation baseAnalyticsElementId={elementId("navigation")} />

        <div className="dl-hero-copy">
          <p className="dl-hero-brand">Evangelist</p>
          <h1 id="desktop-hero-heading">Clipping &amp; UGC Platform for Professional Media Buyers</h1>
          <p>All the accountability and control that your brand demands from paid media. All under one platform.</p>
          <ScheduleLink analyticsElementId={elementId("schedule-demo")}>Schedule a Demo</ScheduleLink>
        </div>

        {/* Above the fold, and the middle card is the LCP candidate, so no `LandingImage` — it
            lazy-loads. One figure: the three cards read as a single reporting view. */}
        <div
          className="dl-hero-reporting"
          role="img"
          aria-label="Evangelist campaign reporting: views by creator platform, daily view distribution, and delivery by country"
        >
          <img
            className="dl-hero-card dl-hero-card--platforms"
            src={landingHeroCreatorPlatforms}
            alt=""
            width={384}
            height={303}
            decoding="async"
          />
          <img
            className="dl-hero-card dl-hero-card--daily"
            src={landingHeroDailyViews}
            alt=""
            width={440}
            height={325}
            decoding="async"
            fetchPriority="high"
          />
          <img
            className="dl-hero-card dl-hero-card--countries"
            src={landingHeroCountries}
            alt=""
            width={315}
            height={183}
            decoding="async"
          />
        </div>
      </div>

      {/* Not "partners" — the distribution partners below are a different set. */}
      <div className="dl-logo-rail" aria-label="Companies working with Evangelist">
        <div className="dl-logo-track">
          {railRepeats.map((repeat) => (
            <div className="dl-logo-group" key={repeat} aria-hidden={repeat > 0 || undefined}>
              {railLogos.map((logo) => (
                <LandingImage
                  key={logo.name}
                  src={logo.src}
                  alt={repeat ? "" : logo.name}
                  width={Math.round(logo.artworkHeight * RAIL_LOGO_SCALE * logo.aspect)}
                  height={Math.round(logo.artworkHeight * RAIL_LOGO_SCALE)}
                  className="dl-partner-logo"
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowClippingWorks() {
  return (
    <section id="how-clipping-works" className="dl-section" aria-labelledby="desktop-how-heading">
      <div className="dl-section-heading">
        <p className="dl-eyebrow">How clipping works</p>
        <h2 id="desktop-how-heading">
          Your content. 1000s of clips. <br />
          Distributed by 100s of creators.
        </h2>
        <p>
          Consumed like organic social. Bought like paid social. Paid clipping turns existing assets into a short-form
          content distribution engine. Clips are designed for social consumption by independent creators.
        </p>
      </div>
      <ol className="dl-steps">
        {clippingSteps.map((step) => (
          <li key={step.title}>
            {/* The heading names the step, so the mark beside it carries nothing an assistive
                reader needs. */}
            <img className="dl-step-icon" src={step.icon} alt="" width={48} height={48} aria-hidden="true" />
            <h3>{step.title}</h3>
            <p>{step.description}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Each desktop tile is as wide as its wordmark, the way the design draws the row. Phones set them
 * in a 2x2 grid at the widths the phone frame gives them (`phoneWidth`), which are not one scale.
 */
const distributionPartners = [
  { name: "Fixated", src: landingFixated, width: 177, height: 21, phoneWidth: 115 },
  { name: "Vyro", src: landingVyro, width: 83, height: 21, phoneWidth: 72 },
  { name: "Content Rewards", src: landingContentRewards, width: 102, height: 31, phoneWidth: 89 },
  { name: "SuperClip", src: landingSuperclip, width: 167, height: 21, phoneWidth: 145 },
];
const publishingPlatforms = [
  { name: "YouTube Shorts", src: landingShorts, width: 39 },
  { name: "Instagram Reels", src: landingInstagram, width: 48 },
  { name: "TikTok", src: landingTiktok, width: 48 },
  { name: "X", src: landingX, width: 47 },
  { name: "Snapchat", src: landingSnapchat, width: 48 },
];
const briefInputs = [
  { label: "Budget", icon: landingFlowDollar },
  { label: "Goal", icon: landingFlowTarget },
  { label: "Audience", icon: landingFlowUsers },
  { label: "Assets", icon: landingFlowVideo },
];
const infrastructureSteps = [
  { label: "Ingest Assets & Set Rules", icon: landingFlowFolders },
  { label: "Distribute Across Marketplaces", icon: landingFlowRoute },
  { label: "Verify Content & Views", icon: landingFlowShield },
  { label: "Track & Pay for Results", icon: landingFlowChart },
];

/** A labelled tile under a diagram card's heading; the label names it, so the glyph is decorative. */
function FlowTile({ icon, label }: { icon: string; label: string }) {
  return (
    <li>
      <img src={icon} alt="" width={40} height={40} aria-hidden="true" />
      <span>{label}</span>
    </li>
  );
}

function Distribution() {
  return (
    <section
      id="cross-marketplace"
      className="dl-section dl-distribution"
      aria-labelledby="desktop-distribution-heading"
    >
      <div className="dl-distribution-copy">
        <p className="dl-eyebrow">Cross-marketplace buying</p>
        <h2 id="desktop-distribution-heading">Clippers Are Not Proprietary</h2>
        <p>
          Evangelist connects your brand to over 500,000 clippers across leading marketplaces and private networks in
          order to find the absolute best creators for your campaign and maximize cost efficiency.
        </p>
      </div>
      <figure
        className="dl-flow"
        aria-label="A campaign brief flows through Evangelist to clipping partners and social platforms"
      >
        <div className="dl-flow-card dl-brief">
          <p className="dl-flow-eyebrow">Campaign Brief</p>
          <h3>Product Launch</h3>
          <ul className="dl-flow-tiles">
            {briefInputs.map((input) => (
              <FlowTile key={input.label} {...input} />
            ))}
          </ul>
        </div>
        <div className="dl-flow-card dl-flow-engine">
          <div className="dl-flow-engine-background" style={{ backgroundImage: `url(${landingHeroBackground})` }} />
          <p className="dl-flow-eyebrow">Our Workflow</p>
          <div className="dl-flow-engine-name">
            <LandingImage src={logoVector} alt="" width={32} height={32} />
            <h3>Evangelist</h3>
          </div>
          <ul className="dl-flow-tiles">
            {infrastructureSteps.map((step) => (
              <FlowTile key={step.label} {...step} />
            ))}
          </ul>
        </div>
        <div className="dl-flow-card dl-flow-partners">
          <p className="dl-flow-eyebrow">Our Partners</p>
          <h3>Campaign Distribution</h3>
          <div className="dl-flow-partner-list">
            <ul className="dl-distribution-logos">
              {distributionPartners.map((logo) => (
                <li key={logo.name} style={{ "--logo-phone-width": `${logo.phoneWidth}px` } as CSSProperties}>
                  <LandingImage src={logo.src} alt={logo.name} width={logo.width} height={logo.height} />
                </li>
              ))}
            </ul>
            <p className="dl-distribution-more">+ Dozens of more partners</p>
          </div>
        </div>
        <div className="dl-flow-card dl-flow-platforms">
          <p className="dl-flow-eyebrow">Supported Platforms</p>
          <h3>Published Clips</h3>
          <ul className="dl-flow-tiles dl-platform-logos">
            {publishingPlatforms.map((platform) => (
              <li key={platform.name}>
                <LandingImage src={platform.src} alt="" width={platform.width} height={48} />
                <span>{platform.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </figure>
    </section>
  );
}

function Services({ baseAnalyticsElementId }: DesktopLandingProps) {
  return (
    <section id="services" className="dl-section dl-services" aria-labelledby="desktop-services-heading">
      <div className="dl-section-heading">
        <p className="dl-eyebrow">Ways to work with Evangelist</p>
        <h2 id="desktop-services-heading">
          Run it yourself.
          <br />
          Or bring us into your team.
        </h2>
        <p>Choose the level of support that fits your team, your campaign and your experience with clipping.</p>
      </div>
      <div className="dl-service-grid">
        {serviceOptions.map((service) => (
          <article className="dl-service-card" key={service.title}>
            {/* The heading names the option, so the mark above it carries nothing an assistive
                reader needs. */}
            <img className="dl-service-icon" src={service.icon} alt="" width={48} height={48} aria-hidden="true" />
            <h3>{service.title}</h3>
            <p>{service.description}</p>
          </article>
        ))}
      </div>
      <ScheduleLink analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, "schedule-meeting")}>
        Schedule a Meeting
      </ScheduleLink>
    </section>
  );
}

function DesktopFaq({ baseAnalyticsElementId }: DesktopLandingProps) {
  const items = useMemo<AccordionItemConfig[]>(
    () =>
      landingFaqItems.map((item) => ({
        id: `desktop-faq-${item.id}`,
        title: item.question,
        content: item.answer,
        defaultOpen: "defaultOpen" in item && item.defaultOpen,
        analyticsElementId: deriveAnalyticsElementId(baseAnalyticsElementId, item.id),
        collapsedIcon: <Plus size={24} aria-hidden="true" />,
        expandedIcon: <Minus size={24} aria-hidden="true" />,
        titleProps: { className: "dl-faq-question" },
        triggerClassName: "dl-faq-trigger",
        contentInnerClassName: "dl-faq-answer",
      })),
    [baseAnalyticsElementId],
  );

  return (
    <section id="desktop-faq" className="dl-section dl-faq" aria-labelledby="desktop-faq-heading">
      <div className="dl-faq-heading">
        <p className="dl-eyebrow">Frequently Asked Questions</p>
        <h2 id="desktop-faq-heading">A few things worth knowing.</h2>
      </div>
      <Accordion items={items} showDividers className="dl-faq-accordion" dividerClassName="dl-faq-divider" />
    </section>
  );
}

function Blog({ baseAnalyticsElementId }: DesktopLandingProps) {
  return (
    <section id="blog" className="dl-section dl-blog" aria-labelledby="desktop-blog-heading">
      <div className="dl-section-heading">
        <p className="dl-eyebrow">Blog</p>
        <h2 id="desktop-blog-heading">Blog</h2>
        <p>{BLOG_DESCRIPTION}</p>
      </div>
      <ArticleGrid
        articles={articleRegistry.slice(0, 3)}
        baseAnalyticsElementId={baseAnalyticsElementId}
        className="dl-article-grid"
      />
      <CtaLink
        analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, "all-articles")}
        to={BLOG_PATH}
        className="dl-cta"
      >
        See All Articles
      </CtaLink>
    </section>
  );
}

function Demo({ baseAnalyticsElementId }: DesktopLandingProps) {
  return (
    <section id="request-demo" className="dl-section dl-demo" aria-labelledby="desktop-demo-heading">
      <div className="dl-section-heading">
        <h2 id="desktop-demo-heading">Let’s put your content to work.</h2>
        <p>
          Tell us what you want to promote. We’ll walk you through the platform and explore a campaign setup that fits
          your goals, assets and budget.
        </p>
      </div>
      <DemoRequestForm baseAnalyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, "demo-request")} />
      <ExternalLink
        analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, "schedule-demo")}
        href={schedulingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="dl-text-link"
      >
        Or book a time directly <ArrowUpRight size={18} aria-hidden="true" />
      </ExternalLink>
    </section>
  );
}

function DesktopFooter({ baseAnalyticsElementId }: DesktopLandingProps) {
  const elementId = (suffix: string) => deriveAnalyticsElementId(baseAnalyticsElementId, suffix);

  return (
    <footer className="dl-section dl-footer">
      <div className="dl-footer-heading">
        <h2>Interested in partnering with Evangelist?</h2>
        <ScheduleLink analyticsElementId={elementId("contact")}>Contact Us</ScheduleLink>
      </div>
      <div className="dl-footer-columns">
        <nav aria-label="Footer audiences">
          <InternalLink analyticsElementId={elementId("clipping-platforms")} href="/for-clipping-platforms">
            For Clipping Platforms
          </InternalLink>
          <InternalLink analyticsElementId={elementId("agencies")} href="/for-agencies">
            For Agencies & Brands
          </InternalLink>
        </nav>
        <nav aria-label="Footer legal">
          <InternalLink analyticsElementId={elementId("privacy-policy")} href="/privacy-policy">
            Privacy Policy
          </InternalLink>
          <InternalLink analyticsElementId={elementId("terms-of-service")} href="/terms-of-service">
            Campaign Participation Terms
          </InternalLink>
        </nav>
        <div className="dl-footer-contact">
          <Logo className="h-7 w-[141px]" imageSizes="141px" />
          <ExternalLink analyticsElementId={elementId("email")} href="mailto:dusan@evangelist.so">
            dusan@evangelist.so
          </ExternalLink>
          <p>Momentist, Inc. © {new Date().getFullYear()} All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

/** One content tree with responsive desktop and mobile layouts; the parent owns page metadata and main. */
export function DesktopLanding({
  baseAnalyticsElementId,
  apiSection,
}: DesktopLandingProps & {
  /** Future API overview, between services and FAQ. Omitted until its content is ready. */
  apiSection?: ReactNode;
}) {
  const sectionId = (suffix: string) => deriveAnalyticsElementId(baseAnalyticsElementId, suffix);

  return (
    <div className="desktop-landing">
      <DesktopHero baseAnalyticsElementId={sectionId("hero")} />
      <HowClippingWorks />
      <Distribution />
      <ProductFeatures baseAnalyticsElementId={sectionId("product-features")} />
      <Services baseAnalyticsElementId={sectionId("services")} />
      {apiSection}
      <DesktopFaq baseAnalyticsElementId={sectionId("faq")} />
      <Blog baseAnalyticsElementId={sectionId("blog")} />
      <Demo baseAnalyticsElementId={sectionId("demo")} />
      <DesktopFooter baseAnalyticsElementId={sectionId("footer")} />
    </div>
  );
}
